import { useState, useEffect } from "react";
import client from "../lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, MagnifyingGlass, Users, User } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";

const EMPTY_DISCOUNT = { code: "", kind: "percent", value: "", max_uses: "", description: "", active: true, allowed_emails: [] };

export default function DiscountsPanel() {
  const [discounts, setDiscounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [discDlg, setDiscDlg] = useState(false);
  const [editingDisc, setEditingDisc] = useState(null);
  const [dForm, setDForm] = useState(EMPTY_DISCOUNT);
  const [memberSearch, setMemberSearch] = useState("");

  const loadDiscounts = () => client.get("/discounts").then((r) => setDiscounts(r.data)).catch(() => {});
  const loadUsers = () => client.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { loadDiscounts(); loadUsers(); }, []);

  const openAddDisc = () => { setEditingDisc(null); setDForm(EMPTY_DISCOUNT); setMemberSearch(""); setDiscDlg(true); };
  const openEditDisc = (d) => {
    setEditingDisc(d);
    setDForm({ code: d.code, kind: d.kind, value: String(d.value), max_uses: d.max_uses == null ? "" : String(d.max_uses), description: d.description || "", active: d.active, allowed_emails: d.allowed_emails || [] });
    setMemberSearch("");
    setDiscDlg(true);
  };
  const saveDisc = async () => {
    if (!editingDisc && !dForm.code.trim()) { toast.error("Code required"); return; }
    if (!(Number(dForm.value) > 0)) { toast.error("Value must be greater than 0"); return; }
    const payload = { kind: dForm.kind, value: Number(dForm.value), active: dForm.active,
      max_uses: dForm.max_uses === "" ? null : Number(dForm.max_uses), description: dForm.description,
      allowed_emails: dForm.allowed_emails };
    try {
      if (editingDisc) { await client.patch(`/discounts/${editingDisc.id}`, payload); toast.success("Code updated"); }
      else { await client.post("/discounts", { ...payload, code: dForm.code.trim() }); toast.success("Discount code created"); }
      setDiscDlg(false); loadDiscounts();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggleDisc = async (d) => {
    try { await client.patch(`/discounts/${d.id}`, { active: !d.active }); loadDiscounts(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const delDisc = async (id) => { await client.delete(`/discounts/${id}`); toast.success("Code deleted"); loadDiscounts(); };

  const toggleEmail = (email) => setDForm((f) => {
    const has = f.allowed_emails.includes(email);
    return { ...f, allowed_emails: has ? f.allowed_emails.filter((e) => e !== email) : [...f.allowed_emails, email] };
  });

  const activeUsers = users.filter((u) => u.active !== false);
  const filteredMembers = activeUsers.filter((u) => {
    const q = memberSearch.trim().toLowerCase();
    if (!q) return true;
    return (u.email || "").toLowerCase().includes(q) || (u.name || "").toLowerCase().includes(q);
  });

  return (
    <div data-testid="discounts-panel">
      <div className="flex justify-end mb-4">
        <Button data-testid="add-discount-button" onClick={openAddDisc} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white"><Plus size={18} className="mr-1" />Add Code</Button>
      </div>
      <div className="border border-[#E9DDC6] rounded-md overflow-hidden bg-white">
        <table className="w-full text-sm" data-testid="discounts-table">
          <thead><tr className="bg-[#FAF9F6] text-left text-[#5C5A58]"><th className="p-4 font-medium">Code</th><th className="p-4 font-medium">Discount</th><th className="p-4 font-medium">Limited to</th><th className="p-4 font-medium">Usage</th><th className="p-4 font-medium">Active</th><th className="p-4 text-right">Actions</th></tr></thead>
          <tbody>
            {discounts.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-[#5C5A58]">No discount codes yet. Create one to offer agents a deal at checkout.</td></tr>}
            {discounts.map((d, i) => {
              const restricted = (d.allowed_emails || []).length > 0;
              return (
              <tr key={d.id} className={i % 2 ? "bg-[#FBFAF7]" : "bg-white"} data-testid={`discount-row-${d.id}`}>
                <td className="p-4"><span className="font-mono-num font-semibold text-[#4A0000]">{d.code}</span>{d.description && <div className="text-xs text-[#5C5A58]">{d.description}</div>}</td>
                <td className="p-4">{d.kind === "percent" ? `${d.value}% off` : `$${d.value} off`}</td>
                <td className="p-4" data-testid={`discount-limit-${d.id}`}>
                  {restricted
                    ? <Badge className="bg-[#B08D57]/15 text-[#8a6d0a] border-[#B08D57]/30" title={d.allowed_emails.join(", ")}><User size={13} className="mr-1" />{d.allowed_emails.length} member{d.allowed_emails.length > 1 ? "s" : ""}</Badge>
                    : <span className="text-[#5C5A58] inline-flex items-center gap-1"><Users size={14} />Everyone</span>}
                </td>
                <td className="p-4 font-mono-num">{d.used_count}{d.max_uses != null ? ` / ${d.max_uses}` : " / ∞"}</td>
                <td className="p-4"><Switch data-testid={`discount-toggle-${d.id}`} checked={d.active} onCheckedChange={() => toggleDisc(d)} /></td>
                <td className="p-4">
                  <div className="flex items-center justify-end gap-3">
                    <button onClick={() => openEditDisc(d)} className="text-[#4A0000] hover:text-[#5C1A1A]" data-testid={`edit-discount-${d.id}`} title="Edit code"><PencilSimple size={16} /></button>
                    <button onClick={() => delDisc(d.id)} className="text-red-600 hover:text-red-800" data-testid={`del-discount-${d.id}`} title="Delete code"><Trash size={16} /></button>
                  </div>
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>

      <Dialog open={discDlg} onOpenChange={setDiscDlg}>
        <DialogContent className="bg-[#FBFAF7] max-h-[90vh] overflow-y-auto" data-testid="discount-dialog">
          <DialogHeader><DialogTitle className="font-display text-3xl">{editingDisc ? "Edit Discount Code" : "Add Discount Code"}</DialogTitle><DialogDescription>Agents can apply this at checkout to reduce their invoice.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Code</Label><Input data-testid="discount-code" value={dForm.code} disabled={!!editingDisc} onChange={(e) => setDForm({ ...dForm, code: e.target.value.toUpperCase() })} placeholder="SAVE20" className="bg-white border-[#E9DDC6] font-mono-num uppercase disabled:bg-[#FAF9F6]" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Type</Label>
                <Select value={dForm.kind} onValueChange={(v) => setDForm({ ...dForm, kind: v })}>
                  <SelectTrigger className="bg-white border-[#E9DDC6]" data-testid="discount-kind"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="percent">Percent (%)</SelectItem><SelectItem value="fixed">Fixed ($)</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>{dForm.kind === "percent" ? "Percent off" : "Amount off ($)"}</Label><Input data-testid="discount-value" type="number" value={dForm.value} onChange={(e) => setDForm({ ...dForm, value: e.target.value })} className="bg-white border-[#E9DDC6] font-mono-num" /></div>
            </div>
            <div><Label>Usage limit <span className="text-[#5C5A58] font-normal text-xs">(blank = unlimited)</span></Label><Input data-testid="discount-maxuses" type="number" value={dForm.max_uses} onChange={(e) => setDForm({ ...dForm, max_uses: e.target.value })} placeholder="Unlimited" className="bg-white border-[#E9DDC6] font-mono-num" /></div>
            <div><Label>Description</Label><Input data-testid="discount-desc" value={dForm.description} onChange={(e) => setDForm({ ...dForm, description: e.target.value })} placeholder="Launch promo" className="bg-white border-[#E9DDC6]" /></div>

            <div className="border-t border-[#F0ECE2] pt-3">
              <Label>Limit to specific members <span className="text-[#5C5A58] font-normal text-xs">(leave empty = everyone)</span></Label>
              <div className="relative mt-1">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9A968E]" />
                <Input data-testid="discount-member-search" value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)} placeholder="Search members by name or email…" className="pl-9 bg-white border-[#E9DDC6]" />
              </div>
              {dForm.allowed_emails.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid="discount-selected-emails">
                  {dForm.allowed_emails.map((em) => (
                    <Badge key={em} className="bg-[#4A0000]/10 text-[#4A0000] border-[#4A0000]/20 cursor-pointer" data-testid={`discount-selected-${em}`} onClick={() => toggleEmail(em)} title="Click to remove">{em} ×</Badge>
                  ))}
                </div>
              )}
              <div className="mt-2 max-h-48 overflow-y-auto border border-[#E9DDC6] rounded-md bg-white divide-y divide-[#F0ECE2]" data-testid="discount-member-list">
                {filteredMembers.length === 0 && <p className="p-3 text-sm text-[#5C5A58]">No members match.</p>}
                {filteredMembers.map((u) => {
                  const checked = dForm.allowed_emails.includes(u.email);
                  return (
                    <label key={u.id} data-testid={`discount-member-${u.email}`} className="flex items-center gap-3 p-2.5 text-sm cursor-pointer hover:bg-[#FBF8F1]">
                      <input type="checkbox" checked={checked} onChange={() => toggleEmail(u.email)} className="accent-[#4A0000] w-4 h-4" />
                      <span className="flex-1 min-w-0">
                        <span className="font-medium">{u.name || u.email}</span>
                        <span className="block text-xs text-[#5C5A58] font-mono-num truncate">{u.email}</span>
                      </span>
                      <Badge className="bg-[#FAF9F6] text-[#5C5A58] border-[#E9DDC6] capitalize">{u.role}</Badge>
                    </label>
                  );
                })}
              </div>
            </div>

            <label className="flex items-center gap-3 text-sm cursor-pointer"><Switch data-testid="discount-active" checked={dForm.active} onCheckedChange={(v) => setDForm({ ...dForm, active: v })} /> Active (agents can use it)</label>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDiscDlg(false)} className="border-[#E9DDC6]">Cancel</Button><Button data-testid="discount-save" onClick={saveDisc} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white">{editingDisc ? "Save changes" : "Add code"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
